import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { breakpoints, useIsCompactMode } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, SkeletonLoader, Text, useModalControl } from '@frontend/design-system';
import { AnalyticsInfoPopover } from '../info-popover';
import { KpiCardProps } from './types';

export const KpiCard = ({
  title,
  value,
  infoTipData,
  iconData,
  subValue,
  subValueText,
  EditKpiCardData,
  isLoading = false,
}: KpiCardProps) => {
  const isCompactMode = useIsCompactMode();
  const { triggerProps, modalProps } = useModalControl();

  return (
    <div css={styles.card}>
      <div css={styles.headerRow}>
        <span css={styles.titleGroup}>
          {title}
          {infoTipData && (
            <span style={{ display: 'inline-flex', verticalAlign: 'sub', marginLeft: theme.spacing(0.5) }}>
              <AnalyticsInfoPopover {...infoTipData} />
            </span>
          )}
        </span>

        {iconData?.name && !isCompactMode && (
          <i css={styles.icon(iconData)}>
            <Icon name={iconData.name as IconName} size={16} />
          </i>
        )}
      </div>

      <div css={styles.valueRow}>
        {isLoading ? (
          <SkeletonLoader
            animation='shimmer'
            shape='rectangle'
            width='60%'
            height={32}
            css={{
              marginBlock: theme.spacing(0.5),
            }}
          />
        ) : (
          <div css={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
            <Text css={styles.value}>{value}</Text>
            {!!subValue && <Text css={styles.subValue}>({subValue})</Text>}
            {!!subValueText && <Text css={styles.subValueText}>{subValueText}</Text>}
          </div>
        )}
      </div>

      {EditKpiCardData && !isCompactMode && (
        <div css={styles.editWrapper}>
          <Text css={styles.editText}>{EditKpiCardData.text}</Text>
          {EditKpiCardData.isEditable && (
            <Button css={styles.editIcon} iconName='edit-small' variant='secondary' {...triggerProps} />
          )}
          <EditKpiCardData.onClickComponent {...modalProps} />
        </div>
      )}
    </div>
  );
};

const styles = {
  card: css`
    border-radius: ${theme.spacing(1)};
    border: 1px solid ${theme.colors.neutral20};
    background: ${theme.colors.white};
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1 1 0;
    min-width: 0;
    align-self: stretch;

    @media (max-width: ${breakpoints.large.min}px) {
      flex: 0 0 calc(50% - ${theme.spacing(1)});
      padding: ${theme.spacing(1.5)};
    }
  `,

  headerRow: css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing(2)};
  `,

  titleGroup: css`
    flex: 1;
    min-width: 0;
    font-size: ${theme.fontSize(16)};
    font-weight: ${theme.font.weight.bold};
    align-items: center;
  `,

  icon: (iconData: NonNullable<KpiCardProps['iconData']>) => css`
    color: ${iconData.color};
    background: ${iconData.bgColor};
    padding: ${theme.spacing(0.5)};
    border-radius: ${theme.spacing(0.5)};
    flex-shrink: 0;
  `,

  valueRow: css`
    display: flex;
    flex-direction: column;
    align-items: baseline;
  `,

  value: css`
    font-size: ${theme.fontSize(36)};
    font-weight: ${theme.font.weight.bold};
    line-height: ${theme.spacing(5)};
    flex-shrink: 0;

    @media (max-width: ${breakpoints.small.max}px) {
      font-size: ${theme.fontSize(24)};
      line-height: ${theme.spacing(4)};
    }
  `,

  subValue: css`
    margin-bottom: ${theme.spacing(0.5)};
    margin-left: ${theme.spacing(0.5)};

    @media (max-width: ${breakpoints.small.max}px) {
      font-size: ${theme.fontSize(12)};
    }
  `,

  subValueText: css`
    font-size: ${theme.fontSize(16)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${breakpoints.small.max}px) {
      font-size: ${theme.fontSize(12)};
    }
  `,

  editWrapper: css`
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing(0.5)};
  `,

  editText: css`
    font-size: ${theme.fontSize(12)};
    line-height: ${theme.spacing(1.75)};
  `,

  editIcon: css`
    padding: 0;
    height: 16px;
    width: 16px;
    color: ${theme.colors.neutral50};
  `,
};
